<template>
  <div class="emotion-market">
    <!-- 顶部导航栏 - 简洁版 -->
    <div class="market-header">
      <div class="header-content">
        <div class="title-section">
        <h1 class="market-title">表情市场</h1>
          <!-- 关于我们按钮 - 与标题并列 -->
          <el-button 
            text 
            @click="showAboutDialog = true"
            class="about-button"
          >
            <el-icon><InfoFilled /></el-icon>
            关于我们
          </el-button>
        </div>
        <!-- 搜索栏 -->
        <div class="search-container">
          <el-input
            v-model="searchQuery"
            placeholder="搜索表情包..."
            clearable
            size="large"
            @keyup.enter="handleSearch"
            class="search-input"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
          <el-button 
            @click="handleSearch" 
            type="primary" 
            size="large"
            class="search-button"
          >
            搜索
          </el-button>
          <!-- 用户信息入口 -->
          <div class="user-info-container">
            <el-dropdown v-if="currentUser" @command="handleUserCommand" trigger="click">
              <div class="user-avatar-wrapper">
                <el-avatar 
                  :size="36" 
                  :src="currentUser.avatar_url"
                  class="user-avatar"
                >
                  <el-icon><UserFilled /></el-icon>
                </el-avatar>
                <span class="user-name">{{ currentUser.nickname || currentUser.username || '用户' }}</span>
                <el-icon class="dropdown-icon"><ArrowDown /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">个人信息</el-dropdown-item>
                  <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-button 
              v-else
              type="primary" 
              plain
              size="large"
              @click="goToLogin"
              class="login-button"
            >
              <el-icon><UserFilled /></el-icon>
              登录
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 瀑布流布局 -->
    <div v-loading="loading" class="masonry-container">
      <div
        v-for="emotion in filteredEmotions"
        :key="emotion.id"
        class="masonry-item"
        @click="viewDetail(emotion)"
      >
        <div class="emotion-card">
          <!-- 图片区域 -->
          <div class="card-image-wrapper">
            <img
              :src="getCoverImage(emotion)"
              :alt="emotion.name"
              class="card-image"
              @error="handleImageError"
              @load="onImageLoad"
            />
            <!-- 悬停遮罩 -->
            <div class="card-overlay">
              <div class="overlay-content">
                <div class="overlay-stats">
                  <span class="stat-item">
                    <el-icon><User /></el-icon>
                    {{ emotion.purchase_count || 0 }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 信息区域 -->
          <div class="card-info">
            <h3 class="card-title">{{ emotion.name }}</h3>
            <div class="card-meta">
              <span class="card-version">v{{ emotion.version }}</span>
              <div class="card-hot" v-if="emotion.hot">
                <el-rate 
                  :model-value="emotion.hot || 5" 
                  disabled 
                  :max="5" 
                  size="small"
                  :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
                />
              </div>
            </div>
            <p class="card-content" v-if="emotion.content">
              {{ emotion.content }}
            </p>
            <div class="card-board-types" v-if="getBoardTypeText(emotion)">
              <span class="board-types-label">适配：</span>
              <span class="board-types-value">{{ getBoardTypeText(emotion) }}</span>
            </div>
            <div class="card-footer">
              <span class="purchase-badge">
                <el-icon><User /></el-icon>
                {{ emotion.purchase_count || 0 }} 人已购买
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载更多提示 -->
    <div v-if="loadingMore" class="loading-more">
      <el-icon class="is-loading"><Loading /></el-icon>
      <span>加载中...</span>
    </div>
    
    <!-- 没有更多提示 -->
    <div v-if="!hasMore && emotions.length > 0 && !loading" class="no-more">
      <span>没有更多了</span>
    </div>
    
    <!-- 空状态 -->
    <el-empty 
      v-if="!loading && !loadingMore && filteredEmotions.length === 0" 
      description="暂无表情包"
      :image-size="200"
    />


    <!-- 关于我们对话框 -->
    <el-dialog
      v-model="showAboutDialog"
      width="600px"
      :close-on-click-modal="true"
      :show-close="true"
    >
      <div class="about-content">
        <!-- 公司简介 -->
        <div class="about-section intro-section">
          <div class="intro-slogan">
            <div>AI驱动硬件，智造定义未来</div>
            <div class="intro-slogan-en">AI-powered hardware, redefining the future of intelligent manufacturing.</div>
          </div>
          <p class="intro-text">
            我们提供从高端硬件研发到AI内容生成的全栈式端到端的解决方案。全面革新生产流程，以AI赋能产品卓越品质。
          </p>
          <p class="intro-text intro-text-en">
            We provide full-stack, end-to-end solutions from advanced hardware R&amp;D to AI content generation, 
            reshaping production workflows and empowering product excellence with AI.
          </p>
        </div>
        
        <!-- B端合作 -->
        <div class="about-section partner-section">
          <div class="section-header">
            <el-icon class="section-icon"><UserFilled /></el-icon>
            <h4>寻找B端合伙人</h4>
          </div>
          <p class="section-text">
            现诚招全球代理商及B端项目合作伙伴，共创智能商业新蓝图。
          </p>
          <p class="section-text section-text-en">
            We are seeking global distributors and B2B partners to build the next generation of intelligent business together.
          </p>
        </div>
        
        <!-- 联系方式 -->
        <div class="about-section contact-section">
          <div class="section-header">
            <el-icon class="section-icon"><Message /></el-icon>
            <h4>联系我们</h4>
          </div>
          <div class="contact-list">
            <div class="contact-item" v-if="contactInfo.phone">
              <el-icon class="contact-icon"><Phone /></el-icon>
              <a :href="`tel:${contactInfo.phone}`" class="contact-link">{{ contactInfo.phone }}</a>
            </div>
            <div class="contact-item" v-if="contactInfo.wechat">
              <el-icon class="contact-icon"><ChatLineRound /></el-icon>
              <div class="wechat-info">
                <span class="contact-text">微信号：</span>
                <span class="wechat-id">{{ contactInfo.wechat }}</span>
                <span class="wechat-note">（添加请注明来意）</span>
              </div>
            </div>
            <div class="contact-item" v-if="contactInfo.address">
              <el-icon class="contact-icon"><Location /></el-icon>
              <span class="contact-text">{{ contactInfo.address }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 详情对话框 -->
    <el-dialog
      v-model="showDetailDialog"
      :title="currentEmotion?.name"
      width="900px"
      :close-on-click-modal="false"
      class="detail-dialog"
    >
      <div v-if="currentEmotion" class="emotion-detail">
        <div class="detail-header">
          <div class="detail-cover">
            <img
              :src="getCoverImage(currentEmotion)"
              :alt="currentEmotion.name"
              @error="handleImageError"
            />
          </div>
          <div class="detail-info">
            <h2>{{ currentEmotion.name }}</h2>
            <p class="detail-version">版本: {{ currentEmotion.version }}</p>
            <div class="detail-stats">
              <span class="purchase-count">
                <el-icon><User /></el-icon>
                {{ currentEmotion.purchase_count || 0 }} 人已购买
              </span>
            </div>
            <div class="detail-hot" v-if="currentEmotion.hot">
              <span class="hot-label">火爆程度：</span>
              <el-rate :model-value="currentEmotion.hot || 5" disabled :max="5" />
            </div>
            <div class="detail-board-types" v-if="getBoardTypeText(currentEmotion)">
              <span class="board-types-label">适配：</span>
              <span class="board-types-value">{{ getBoardTypeText(currentEmotion) }}</span>
            </div>
            <p class="detail-content" v-if="currentEmotion.content">
              {{ currentEmotion.content }}
            </p>
          </div>
        </div>

        <!-- 预览图 -->
        <div v-if="currentEmotion.preview_images && currentEmotion.preview_images.length > 0" class="preview-images">
          <h3>预览图</h3>
          <div class="preview-grid">
            <img
              v-for="(img, index) in currentEmotion.preview_images"
              :key="index"
              :src="getPreviewImageUrl(img)"
              :alt="`预览图 ${index + 1}`"
              @error="handleImageError"
              @click="previewImage(getPreviewImageUrl(img))"
            />
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog
      v-model="showImagePreview"
      width="90%"
      :show-close="true"
      align-center
      class="image-preview-dialog"
    >
      <img :src="previewImageUrl" class="preview-image" />
    </el-dialog>

    <!-- 联系客服悬浮窗 -->
    <div class="customer-service-float" :class="{ 'expanded': showServicePanel }">
      <!-- 悬浮按钮 -->
      <div class="service-button" @click="toggleServicePanel">
        <el-icon v-if="!showServicePanel" class="service-icon"><ChatLineRound /></el-icon>
        <el-icon v-else class="service-icon"><Close /></el-icon>
      </div>
      
      <!-- 客服面板 -->
      <div v-if="showServicePanel" class="service-panel">
        <div class="service-header">
          <h3>联系客服</h3>
        </div>
        <div class="service-content">
          <div class="service-item" @click="callPhone">
            <el-icon class="service-item-icon"><Phone /></el-icon>
            <div class="service-item-info">
              <div class="service-item-label">联系电话</div>
              <div class="service-item-value">{{ contactInfo.phone }}</div>
            </div>
          </div>
          <div class="service-item" @click="copyWeChat">
            <el-icon class="service-item-icon"><ChatLineRound /></el-icon>
            <div class="service-item-info">
              <div class="service-item-label">微信号</div>
              <div class="service-item-value">{{ contactInfo.wechat }}</div>
            </div>
            <el-icon class="copy-icon"><DocumentCopy /></el-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { ref, computed, onMounted, onUnmounted, nextTick, watch, onActivated } from 'vue';
import { ElMessage } from 'element-plus';
import { User, Search, Loading, InfoFilled, Message, Phone, Location, UserFilled, ChatLineRound, ArrowDown, Close, DocumentCopy } from '@element-plus/icons-vue';
import { getEmotionMarket, getEmotionMarketDetail } from '../api/emotion';
import { getCurrentUser } from '../api/auth';
import { getBoardTypes } from '../api/device';
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router';
/* eslint-enable */
import defaultCover from '@/assets/cover/cover.gif';

// 获取 API base URL（与 emotion.js 中的逻辑保持一致）
const getApiBaseUrl = () => {
  const rawBase = process.env.VUE_APP_API_BASE_URL || '';
  if (rawBase) {
    return rawBase.endsWith('/api') ? rawBase : `${rawBase}/api`;
  }
  return '/api';
};

export default {
  name: 'EmotionMarket',
  components: {
    User,
    Search,
    Loading,
    InfoFilled,
    Message,
    Phone,
    Location,
    UserFilled,
    ChatLineRound,
    ArrowDown,
    Close,
    DocumentCopy
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const loadingMore = ref(false);
    const emotions = ref([]);
    const searchQuery = ref('');
    const showDetailDialog = ref(false);
    const showAboutDialog = ref(false);
    const currentEmotion = ref(null);
    const showImagePreview = ref(false);
    const previewImageUrl = ref('');
    const currentUser = ref(null);
    const showServicePanel = ref(false);
    const boardTypes = ref([]);
    
    // 联系信息配置
    const contactInfo = ref({
      phone: '19946209040', // 联系电话
      wechat: 'Joestation2000', // 微信号
      address: '中国·上海' // 地址
    });
    
    // 分页相关
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const hasMore = ref(true);

    // 过滤表情包（搜索时重置并重新加载）
    const filteredEmotions = computed(() => {
      if (!searchQuery.value) {
        return emotions.value;
      }
      const query = searchQuery.value.toLowerCase();
      return emotions.value.filter(emotion =>
        emotion.name.toLowerCase().includes(query) ||
        (emotion.content && emotion.content.toLowerCase().includes(query))
      );
    });
    
    // 搜索处理（重置并重新加载）
    const handleSearchWithReload = () => {
      if (searchQuery.value) {
        // 有搜索关键词时，前端过滤
        // 注意：这里只是前端过滤，如果需要后端搜索，需要修改API
        return;
      }
      // 无搜索关键词时，重置加载
      loadEmotions(true);
    };

    // 加载表情市场列表（首次加载或重置）
    const loadEmotions = async (reset = false) => {
      if (reset) {
        currentPage.value = 1;
        emotions.value = [];
        hasMore.value = true;
      }
      
      if (!hasMore.value && !reset) {
        return;
      }
      
      // 设置超时保护，防止 loading 一直为 true
      let loadingTimeout = null;
      try {
        if (currentPage.value === 1) {
          loading.value = true;
          // 设置 30 秒超时保护
          loadingTimeout = setTimeout(() => {
            if (loading.value) {
              console.warn('加载表情市场超时，自动关闭 loading');
              loading.value = false;
              ElMessage.warning('加载超时，请刷新页面重试');
            }
          }, 30000);
        } else {
          loadingMore.value = true;
        }
        
        const response = await getEmotionMarket({
          page: currentPage.value,
          pageSize: pageSize.value,
          keyword: searchQuery.value || ''
        });
        if (response.success) {
          const marketData = response.data || {};
          const rawList = Array.isArray(marketData.list)
            ? marketData.list
            : (Array.isArray(response.data) ? response.data : []);
          const newEmotions = rawList.map((item) => ({
            ...item,
            id: item.id || item.pack_id,
            // Backend market payload uses cover_url/origin_url.
            // Normalize to legacy fields expected by this page.
            cover_image: item.cover_image || item.cover_url || '',
            preview_images: Array.isArray(item.preview_images)
              ? item.preview_images
              : (item.origin_url ? [item.origin_url] : [])
          }));
          if (reset) {
            emotions.value = newEmotions;
          } else {
            emotions.value = [...emotions.value, ...newEmotions];
          }
          
          total.value = marketData.total ?? response.total ?? 0;
          const explicitHasMore = marketData.has_more ?? response.has_more;
          hasMore.value = explicitHasMore !== undefined
            ? explicitHasMore
            : newEmotions.length === pageSize.value;
          
          // 等待DOM更新后重新布局
          await nextTick();
          adjustMasonryLayout();
        } else {
          ElMessage.error(response.message || '加载表情市场失败');
        }
      } catch (error) {
        console.error('加载表情市场失败:', error);
        ElMessage.error('加载表情市场失败');
      } finally {
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
        loading.value = false;
        loadingMore.value = false;
      }
    };
    
    // 加载更多（无限滚动）
    const loadMore = async () => {
      if (loadingMore.value || !hasMore.value) {
        return;
      }
      currentPage.value += 1;
      await loadEmotions(false);
    };
    
    // 滚动监听（无限滚动）
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // 距离底部100px时加载更多
      if (scrollTop + windowHeight >= documentHeight - 100) {
        loadMore();
      }
    };

    // 调整瀑布流布局
    const adjustMasonryLayout = () => {
      // 使用CSS columns实现瀑布流，这里可以添加额外的优化逻辑
      // 如果需要更精确的控制，可以使用JavaScript库如masonry.js
    };

    // 图片加载完成
    const onImageLoad = () => {
      // 可以在这里添加图片加载完成的处理逻辑
    };

    // 搜索
    const handleSearch = () => {
      handleSearchWithReload();
    };

    // 查看详情
    const viewDetail = async (emotion) => {
      try {
        const response = await getEmotionMarketDetail(emotion.id);
        if (response.success) {
          const emotionData = response.data;
          
          // 在前端再次过滤预览图，确保不显示封面图
          if (emotionData.preview_images && Array.isArray(emotionData.preview_images)) {
            const coverBase = emotionData.cover_image ? emotionData.cover_image.split('?')[0] : '';
            emotionData.preview_images = emotionData.preview_images.filter((url) => {
              if (!url || !url.trim()) return false;
              const urlBase = url.split('?')[0];
              // 过滤掉与封面图相同的URL（比较基础URL）
              return urlBase !== coverBase && url !== emotionData.cover_image;
            });
          }
          currentEmotion.value = emotionData;
          showDetailDialog.value = true;
        } else {
          ElMessage.error(response.message || '获取详情失败');
        }
      } catch (error) {
        console.error('获取详情失败:', error);
        ElMessage.error('获取详情失败');
      }
    };

    // 预览图片
    const previewImage = (url) => {
      if (!url) return;
      let finalUrl = url;
      // 如果是相对路径，需要添加baseUrl
      if (finalUrl.startsWith('/api/')) {
        const baseUrl = getApiBaseUrl();
        if (!finalUrl.startsWith(baseUrl)) {
          finalUrl = `${baseUrl}${finalUrl.replace('/api', '')}`;
        }
      }
      previewImageUrl.value = finalUrl;
      showImagePreview.value = true;
    };

    // 将版型ID转换为名称
    const getBoardTypeNames = (boardTypesStr) => {
      const raw = String(boardTypesStr || '').trim();
      if (!raw) return '';
      const types = raw.replace(/，/g, ',').split(',').map(t => t.trim()).filter(Boolean);
      if (!types.length) return '';
      if (!boardTypes.value.length) return types.join('，');
      const names = types.map((t) => {
        const board = boardTypes.value.find((b) => String(b.id) === String(t));
        return board ? board.name : t;
      });
      return names.join('，');
    };

    // Resolve compatible board label from backend variants.
    const getBoardTypeText = (emotion) => {
      if (!emotion) return '';
      if (emotion.board_type_names && String(emotion.board_type_names).trim()) {
        return String(emotion.board_type_names).trim();
      }
      const candidate =
        emotion.board_types ??
        emotion.board_type ??
        emotion.hardware_type ??
        '';
      return getBoardTypeNames(candidate);
    };

    // 获取封面图（如果没有则使用默认图，并添加admin参数和token）
    const getCoverImage = (emotion) => {
      if (!emotion) {
        return defaultCover;
      }
      
      let imageUrl = emotion.cover_image || defaultCover;
      
      // 如果是默认图，直接返回
      if (!imageUrl || imageUrl === defaultCover || !emotion.cover_image) {
        return defaultCover;
      }
      
      // 如果是相对路径的API图片，需要添加baseUrl
      if (imageUrl.startsWith('/api/')) {
        const baseUrl = getApiBaseUrl();
        let fullUrl = imageUrl;
        // 如果baseUrl不是以/api结尾，需要调整
        if (!imageUrl.startsWith(baseUrl)) {
          if (baseUrl.endsWith('/api')) {
            fullUrl = `${baseUrl}${imageUrl.replace('/api', '')}`;
          } else {
            fullUrl = `${baseUrl}${imageUrl}`;
          }
        }
        // 直接返回URL，无需添加token
        return fullUrl;
      }
      // 如果是完整URL，直接返回
      return imageUrl;
    };
    
    // 获取预览图URL（添加token，确保不是封面图）
    const getPreviewImageUrl = (imageUrl) => {
      if (!imageUrl || !imageUrl.trim()) {
        return '';
      }
      
      // 确保预览图不是封面图（比较去除查询参数后的URL）
      if (currentEmotion.value && currentEmotion.value.cover_image) {
        const coverBase = currentEmotion.value.cover_image.split('?')[0];
        const previewBase = imageUrl.split('?')[0];
        // 比较基础URL（去除查询参数）
        if (previewBase === coverBase || imageUrl === currentEmotion.value.cover_image) {
          return ''; // 如果是封面图，返回空字符串
        }
      }
      
      // 如果是相对路径的API图片，需要添加baseUrl
      if (imageUrl.startsWith('/api/')) {
        const baseUrl = getApiBaseUrl();
        let fullUrl = imageUrl;
        // 如果baseUrl不是以/api结尾，需要调整
        if (!imageUrl.startsWith(baseUrl)) {
          if (baseUrl.endsWith('/api')) {
            fullUrl = `${baseUrl}${imageUrl.replace('/api', '')}`;
          } else {
            fullUrl = `${baseUrl}${imageUrl}`;
          }
        }
        // 直接返回URL，无需添加token
        return fullUrl;
      }
      // 如果是完整URL，直接返回
      return imageUrl;
    };

    // 图片加载错误处理
    const handleImageError = (event) => {
      // 如果加载失败，使用默认图
      if (event.target.src !== defaultCover) {
        event.target.src = defaultCover;
      }
    };

    // 加载当前用户信息
    const loadCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          currentUser.value = null;
          return;
        }
        
        // 使用 skipAutoMessage 避免 401 错误时自动跳转，我们自己处理
        let response;
        try {
          response = await getCurrentUser();
        } catch (err) {
          // 如果是 401 错误，说明 token 无效，但不要立即清除，先尝试使用本地缓存
          // 只有在确认 token 真的无效时，才让 API 拦截器处理
          if (err.response && err.response.status === 401) {
            console.warn('获取用户信息失败：401 未授权，尝试使用本地缓存');
            response = null; // 设置为 null，让后续逻辑使用本地缓存
          } else {
            // 其他错误继续抛出
            throw err;
          }
        }
        
        // API 响应拦截器返回 response.data，所以 response 已经是 {success: true, data: {...}}
        if (response && response.success && response.data && response.data.id) {
          currentUser.value = {
            id: response.data.id,
            username: response.data.username,
            nickname: response.data.nickname,
            avatar_url: response.data.avatar_url,
            role: response.data.role
          };
        } else {
          // 如果获取用户信息失败，但不一定是 token 无效，可能是网络问题
          // 先尝试使用本地缓存的用户信息
          try {
            const userStr = localStorage.getItem('user');
            if (userStr && userStr !== 'undefined') {
              const cachedUser = JSON.parse(userStr);
              currentUser.value = {
                id: cachedUser.id,
                username: cachedUser.username,
                nickname: cachedUser.nickname,
                avatar_url: cachedUser.avatar_url,
                role: cachedUser.role
              };
              console.warn('使用本地缓存的用户信息');
            } else {
              currentUser.value = null;
            }
          } catch (e) {
            currentUser.value = null;
          }
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
        // 如果获取用户信息失败，但不一定是 token 无效，可能是网络问题
        // 先尝试使用本地缓存的用户信息
        try {
          const userStr = localStorage.getItem('user');
          if (userStr && userStr !== 'undefined') {
            const cachedUser = JSON.parse(userStr);
            currentUser.value = {
              id: cachedUser.id,
              username: cachedUser.username,
              nickname: cachedUser.nickname,
              avatar_url: cachedUser.avatar_url,
              role: cachedUser.role
            };
            console.warn('使用本地缓存的用户信息（异常情况）');
          } else {
            currentUser.value = null;
          }
        } catch (e) {
          currentUser.value = null;
        }
        // 注意：不要在这里清除 token，因为 API 拦截器已经处理了 401 错误
      }
    };

    // 处理用户下拉菜单命令
    const handleUserCommand = (command) => {
      if (command === 'logout') {
        // 退出登录
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        currentUser.value = null;
        ElMessage.success('已退出登录');
        // 刷新页面
        window.location.reload();
      } else if (command === 'profile') {
        // 在新窗口打开个人信息页面
        const profileUrl = window.location.origin + '/profile';
        window.open(profileUrl, '_blank');
      }
    };

    // 跳转到登录页，带上当前页面作为redirect参数
    const goToLogin = () => {
      router.push({
        path: '/login',
        query: { redirect: '/market' }
      });
    };

    // 切换客服面板显示/隐藏
    const toggleServicePanel = () => {
      showServicePanel.value = !showServicePanel.value;
    };

    // 拨打电话
    const callPhone = () => {
      window.location.href = `tel:${contactInfo.value.phone}`;
    };

    // 复制微信号
    const copyWeChat = async () => {
      try {
        await navigator.clipboard.writeText(contactInfo.value.wechat);
        ElMessage.success('微信号已复制到剪贴板');
      } catch (err) {
        // 降级方案：使用传统方法复制
        const textArea = document.createElement('textarea');
        textArea.value = contactInfo.value.wechat;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          ElMessage.success('微信号已复制到剪贴板');
        } catch (e) {
          ElMessage.error('复制失败，请手动复制');
        }
        document.body.removeChild(textArea);
      }
    };

    // 监听路由变化，当路由变化到 market 时重新加载用户信息
    watch(() => route.path, (newPath, oldPath) => {
      if (newPath === '/market' && oldPath === '/login') {
        // 从登录页跳转回来时，重新加载用户信息
        loadCurrentUser();
      }
    });

    // 当路由更新时（例如从登录页跳转回来），重新加载用户信息
    onBeforeRouteUpdate((to, from, next) => {
      if (to.path === '/market' && from.path === '/login') {
        loadCurrentUser();
      }
      next();
    });

    const loadBoardTypes = async () => {
      try {
        const response = await getBoardTypes();
        if (response.success) {
          boardTypes.value = response.data;
        }
      } catch (error) {
        console.error('获取版型列表失败:', error);
      }
    };

    onMounted(() => {
      loadCurrentUser();
      loadBoardTypes();
      // 加载表情列表
      loadEmotions(true);
      // 监听窗口大小变化，重新调整布局
      window.addEventListener('resize', adjustMasonryLayout);
      // 监听滚动事件（无限滚动）
      window.addEventListener('scroll', handleScroll, { passive: true });
    });

    // 如果使用了 keep-alive，在组件激活时重新加载用户信息
    onActivated(() => {
      loadCurrentUser();
    });
    
    onUnmounted(() => {
      // 清理事件监听
      window.removeEventListener('resize', adjustMasonryLayout);
      window.removeEventListener('scroll', handleScroll);
    });

    return {
      loading,
      loadingMore,
      emotions,
      searchQuery,
      filteredEmotions,
      showDetailDialog,
      showAboutDialog,
      contactInfo,
      currentEmotion,
      showImagePreview,
      previewImageUrl,
      hasMore,
      currentUser,
      handleSearch,
      viewDetail,
      previewImage,
      getCoverImage,
      getPreviewImageUrl,
      handleImageError,
      onImageLoad,
      handleUserCommand,
      goToLogin,
      showServicePanel,
      toggleServicePanel,
      callPhone,
      copyWeChat,
      getBoardTypeNames,
      getBoardTypeText
    };
  }
};
</script>

<style scoped>
/* 主容器 */
.emotion-market {
  min-height: 100vh;
  background: linear-gradient(to bottom, #f5f7fa 0%, #ffffff 100%);
  padding: 0;
}

/* 顶部导航栏 - 简洁版 */
.market-header {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 20px 0;
  margin-bottom: 24px;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

.market-title {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: -0.3px;
  white-space: nowrap;
}

/* 搜索容器 - 内嵌在头部 */
.search-container {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 600px;
  max-width: 800px;
}

.search-input {
  flex: 1;
}

.search-input :deep(.el-input__wrapper) {
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 0 20px;
}

.search-button {
  border-radius: 24px;
  padding: 0 24px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* 用户信息容器 */
.user-info-container {
  margin-left: 12px;
  flex-shrink: 0;
}

.user-avatar-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 20px;
  transition: background-color 0.3s;
}

.user-avatar-wrapper:hover {
  background-color: #f5f7fa;
}

.user-avatar {
  flex-shrink: 0;
}

.user-name {
  font-size: 14px;
  color: #606266;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-icon {
  font-size: 12px;
  color: #909399;
  transition: transform 0.3s;
}

.login-button {
  border-radius: 20px;
  padding: 0 20px;
  white-space: nowrap;
}

/* 关于我们按钮 */
.about-button {
  white-space: nowrap;
  color: #606266;
  font-size: 14px;
  padding: 4px 8px;
  font-weight: 400;
}

.about-button:hover {
  color: #409EFF;
}

/* 关于我们对话框样式 */
.about-content {
  padding: 16px 0;
}

.about-section {
  margin-bottom: 20px;
}

.about-section:last-child {
  margin-bottom: 0;
}

/* 简介部分 */
.intro-section {
  text-align: left;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f2f5;
}

.intro-slogan {
  font-size: 24px;
  font-weight: 600;
  color: #409EFF;
  margin-bottom: 14px;
  letter-spacing: 1px;
  text-align: center;
}

.intro-slogan-en {
  font-size: 14px;
  font-weight: 400;
  color: #909399;
  margin-top: 6px;
  text-align: center;
}

.intro-text {
  font-size: 15px;
  line-height: 1.8;
  color: #606266;
  margin: 0 0 6px 0;
  max-width: 500px;
}

.intro-text-en {
  font-size: 14px;
  line-height: 1.8;
  color: #909399;
  margin-top: 6px;
  max-width: 520px;
}

/* 通用区块样式 */
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.section-icon {
  font-size: 20px;
  color: #409EFF;
}

.section-header h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.section-text {
  font-size: 15px;
  line-height: 1.8;
  color: #606266;
  margin: 0;
  padding-left: 20px;
}

.section-text-en {
  font-size: 14px;
  line-height: 1.8;
  color: #909399;
  margin-top: 4px;
  padding-left: 20px;
}

/* 合作伙伴区块 */
.partner-section {
  padding: 14px 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-radius: 8px;
  border-left: 4px solid #409EFF;
}

/* 联系方式区块 */
.contact-section {
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 8px;
}

.contact-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 20px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
}

.contact-icon {
  font-size: 18px;
  color: #909399;
  flex-shrink: 0;
}

.contact-link {
  color: #409EFF;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s;
}

.contact-link:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.contact-text {
  color: #606266;
}

.wechat-info {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.wechat-id {
  color: #409EFF;
  font-weight: 500;
}

.wechat-note {
  color: #909399;
  font-size: 13px;
}

/* 瀑布流容器 */
.masonry-container {
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  column-width: 220px;
  column-count: auto;
  column-gap: 12px;
}

@media (max-width: 900px) {
  .masonry-container {
    column-width: 220px;
    padding: 0 12px;
  }
}

@media (max-width: 600px) {
  .masonry-container {
    column-width: auto;
    column-count: 1;
    padding: 0 16px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .title-section {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .market-title {
    font-size: 24px;
    text-align: center;
  }
  
  .search-container {
    min-width: 100%;
    max-width: 100%;
    flex-wrap: wrap;
  }
  
  .user-info-container {
    margin-left: 0;
    margin-top: 8px;
    width: 100%;
  }
  
  .user-info-container .login-button {
    width: 100%;
  }
}

/* 瀑布流项目 */
.masonry-item {
  break-inside: avoid;
  margin-bottom: 12px;
  page-break-inside: avoid;
}

/* 卡片样式 */
.emotion-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.emotion-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.12);
}

/* 图片区域 */
.card-image-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #f5f7fa;
  aspect-ratio: 1;
}

.card-image {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.emotion-card:hover .card-image {
  transform: scale(1.05);
}

/* 悬停遮罩 */
.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.6) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: flex-end;
  padding: 16px;
}

.emotion-card:hover .card-overlay {
  opacity: 1;
}

.overlay-content {
  width: 100%;
}

.overlay-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #fff;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
}

/* 信息区域 */
.card-info {
  padding: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 8px;
}

.card-version {
  font-size: 12px;
  color: #999;
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 12px;
}

.card-hot {
  display: flex;
  align-items: center;
}

.card-hot :deep(.el-rate) {
  height: 16px;
}

.card-content {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.card-board-types {
  font-size: 12px;
  color: #666;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.board-types-label {
  color: #999;
  font-weight: 500;
}

.board-types-value {
  color: #409eff;
  font-weight: 500;
}

.card-footer {
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.purchase-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.purchase-badge .el-icon {
  font-size: 14px;
}

/* 详情对话框 */
.detail-dialog :deep(.el-dialog__header) {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-dialog :deep(.el-dialog__body) {
  padding: 24px;
}

.emotion-detail {
  padding: 0;
}

.detail-header {
  display: flex;
  gap: 24px;
  margin-bottom: 30px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-cover {
  width: 200px;
  height: 200px;
  background: #f5f7fa;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-info {
  flex: 1;
}

.detail-info h2 {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.detail-version {
  font-size: 14px;
  color: #999;
  margin: 0 0 16px 0;
}

.detail-stats {
  margin-bottom: 16px;
}

.purchase-count {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.detail-hot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
}

.hot-label {
  font-size: 14px;
  color: #666;
}

.detail-board-types {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  font-size: 14px;
}

.detail-board-types .board-types-label {
  color: #666;
  font-weight: 500;
}

.detail-board-types .board-types-value {
  color: #409eff;
  font-weight: 500;
}

.detail-content {
  font-size: 16px;
  color: #666;
  line-height: 1.8;
  margin: 0;
}

.preview-images {
  margin-top: 30px;
}

.preview-images h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.preview-grid img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.preview-grid img:hover {
  transform: scale(1.05);
}

/* 图片预览对话框 */
.image-preview-dialog :deep(.el-dialog__body) {
  padding: 0;
  text-align: center;
}

.preview-image {
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

/* 加载更多提示 */
.loading-more {
  text-align: center;
  padding: 40px 0;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
}

.loading-more .el-icon {
  font-size: 18px;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 没有更多提示 */
.no-more {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
}

/* 空状态 */
.el-empty {
  margin: 80px 0;
}

/* 加载状态优化：仅用于视觉提示，不阻挡鼠标事件 */
:deep(.el-loading-mask) {
  background-color: rgba(255, 255, 255, 0.6);
  pointer-events: none; /* 避免遮罩层拦截点击，防止页面不可操作 */
}

/* 联系客服悬浮窗 */
.customer-service-float {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1000;
  transition: all 0.3s ease;
}

.service-button {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.service-button:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
}

.service-icon {
  font-size: 24px;
  color: #fff;
}

.service-panel {
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 280px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.service-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.service-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.service-content {
  padding: 12px;
}

.service-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 8px;
}

.service-item:last-child {
  margin-bottom: 0;
}

.service-item:hover {
  background-color: #f5f7fa;
}

.service-item-icon {
  font-size: 20px;
  color: #667eea;
  margin-right: 12px;
  flex-shrink: 0;
}

.service-item-info {
  flex: 1;
}

.service-item-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.service-item-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.copy-icon {
  font-size: 16px;
  color: #999;
  margin-left: 8px;
  transition: color 0.2s;
}

.service-item:hover .copy-icon {
  color: #667eea;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .customer-service-float {
    right: 16px;
    bottom: 16px;
  }

  .service-button {
    width: 48px;
    height: 48px;
  }

  .service-icon {
    font-size: 20px;
  }

  .service-panel {
    width: 260px;
    bottom: 60px;
  }
}
</style>